import * as React from "react";
import { Check, ChevronDown, CircleX, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui";
import { SelectProps } from "./types";
import { useMemo, useState, useEffect } from "react";
import { Loader } from "../Loader";

const Select: React.FC<SelectProps> = ({
  placeholder = "Select",
  searchPlaceholder = "Search...",
  noDataPlaceholder = "No data found",
  options = [],
  value,
  onChange,
  disabled,
  className,
  multiSelect = false,
  onClear,
  showSearch = true,
  showChip = false,
  onLoadMore,
  isLoadingMore = false,
  hasMore = false,
  onSearchChange,
}) => {
  const [open, setOpen] = React.useState(false);
  const [searchValue, setSearchValue] = useState("");

  const isServerSearch = Boolean(onSearchChange);

  // Debounce API search ONLY when server-side search handler is provided
  useEffect(() => {
    if (!isServerSearch) return;

    const handler = setTimeout(() => {
      onSearchChange?.(searchValue);
    }, 300);

    return () => clearTimeout(handler);
  }, [searchValue, isServerSearch, onSearchChange]);

  // Reset search state on popover close
  useEffect(() => {
    if (!open) {
      setSearchValue("");
      if (isServerSearch) {
        onSearchChange?.("");
      }
    }
  }, [open, isServerSearch, onSearchChange]);

  const selectedValues = React.useMemo(
    () => (Array.isArray(value) ? value : value ? [value] : []),
    [value]
  );

  const selectedLabels = React.useMemo(
    () =>
      selectedValues
        .map((val) => options.find((opt) => opt.value === val)?.label)
        .filter(Boolean) as string[],
    [selectedValues, options]
  );

  const renderDisplayValue = useMemo(() => {
    if (multiSelect && Array.isArray(value)) {
      const labels = value
        ?.map((val) => options?.find((opt) => opt?.value === val)?.label)
        ?.filter(Boolean);

      return labels?.length ? labels?.join(", ") : disabled ? "" : placeholder;
    }

    if (!multiSelect) {
      const selected = options?.find((opt) => opt?.value == value)?.label;
      return selected ? selected : disabled ? "" : placeholder;
    }

    return disabled ? "" : placeholder;
  }, [multiSelect, value, options, disabled, placeholder]);

  const toggleMultiValue = (selectedValue: string) => {
    const updated = selectedValues.includes(selectedValue)
      ? selectedValues.filter((v) => v !== selectedValue)
      : [...selectedValues, selectedValue];

    onChange(updated as any);
  };

  const handleSelect = (selectedValue: string) => {
    if (multiSelect) {
      const currentValues = Array.isArray(value) ? [...value] : [];
      let updated = [...currentValues];
      const isSelected = updated.includes(selectedValue);

      if (isSelected) {
        updated = updated.filter((v) => v !== selectedValue);
      } else {
        updated.push(selectedValue);
      }

      if (selectedValue === "all") {
        updated = ["all"];
      } else {
        updated = updated.filter((v) => v !== "all");
      }

      onChange(updated as any);
    } else {
      onChange(selectedValue);
      setOpen(false);
    }
  };

  const isSelected = (val: string) => {
    return multiSelect && Array.isArray(value)
      ? value.includes(val)
      : value === val;
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const isAtBottom =
      target.scrollHeight - target.scrollTop <= target.clientHeight + 15;

    if (isAtBottom && hasMore && !isLoadingMore && onLoadMore) {
      onLoadMore();
    }
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen} modal={true}>
        <PopoverTrigger
          asChild
          disabled={disabled}
          className={cn("h-[40px] w-full", className)}
        >
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="justify-between font-normal border-gray-200"
          >
            <span className="truncate text-[#2b333f]">
              {renderDisplayValue}
            </span>

            {disabled ? null : onClear && selectedValues.length ? (
              <CircleX
                className="opacity-50 cursor-pointer"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onClear();
                }}
              />
            ) : (
              <ChevronDown className="opacity-50" />
            )}
          </Button>
        </PopoverTrigger>

        <PopoverContent
          className="p-0 w-[var(--radix-popover-trigger-width)] pointer-events-auto z-[9999]"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <Command
            shouldFilter={!isServerSearch}
            className="max-h-[250px] w-full flex flex-col overflow-hidden"
          >
            {showSearch && (
              <CommandInput
                placeholder={searchPlaceholder}
                className="h-9 shrink-0"
                value={isServerSearch ? searchValue : undefined}
                onValueChange={isServerSearch ? setSearchValue : undefined}
              />
            )}

            <CommandList
              className="max-h-[200px] overflow-y-auto overflow-x-hidden pointer-events-auto"
              onScroll={handleScroll}
              onWheel={(e) => e.stopPropagation()}
              onTouchMove={(e) => e.stopPropagation()}
            >
              {options.length === 0 && !isLoadingMore && (
                <CommandEmpty>{noDataPlaceholder}</CommandEmpty>
              )}

              <CommandGroup>
                {options.map((opt) => (
                  <CommandItem
                    key={opt?.value}
                    value={opt?.value}
                    keywords={[opt?.label || ""]}
                    onSelect={() => handleSelect(opt?.value)}
                    disabled={opt?.disabled}
                  >
                    {opt?.label || ""}
                    <Check
                      className={cn(
                        "ml-auto h-4 w-4",
                        isSelected(opt?.value) ? "opacity-100" : "opacity-0"
                      )}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>

              {isLoadingMore && (
                <div className="py-2 flex justify-center">
                  <Loader variant="secondary" className="border-t-primary" />
                </div>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Chips */}
      {showChip && multiSelect && selectedLabels.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2 rounded-md">
          {selectedLabels.map((label, idx) => (
            <div
              key={idx}
              className="flex items-center gap-1 bg-blue-50 text-primary px-3 py-1 rounded-full text-sm"
            >
              <span>{label}</span>
              <X
                className="w-4 cursor-pointer"
                onClick={() => toggleMultiValue(selectedValues[idx])}
              />
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default Select;